import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { prebidForJob } from '../utils/prebid';
import { saveMyCommGoal } from '../utils/api';

/* My commission — on the front of My Dashboard. Only the logged-in person's own jobs.
   Sales rep:   the job's Pre-Bid tab, the Commission Calculator formula
                rate × (price per unit − floor) × quantity (or the commission typed there), times their split.
   Canvasser / telemarketer / confirmer: the Canvasser Payroll rates, on the first two jobs per customer,
                times their split.  Paid once the job is funded / closed.                                   */

const usd = n => (n == null ? '—' : '$' + Math.round(n).toLocaleString('en-US'));
const niceDate = s => (s ? new Date(s + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '');
const blank = v => String(v ?? '').trim() === '';

export function commissionFor(j, c) {
  if (j.role === 'Sales Rep') {
    const pb = prebidForJob(j);
    if (!pb || !Number.isFinite(pb.commission)) return { amt: null, how: 'No Pre-Bid on this job yet' };
    if (pb.manual && blank(j.prebid_commission)) return { amt: null, how: 'Pre-Bid has no commission typed' };
    const how = pb.commissionEntered || pb.manual ? 'typed on the Pre-Bid' : pb.belowFloor ? 'sold at or below the floor' : `${Math.round(pb.rate * 100)}% above the floor`;
    return { amt: pb.commission * j.share, how };
  }
  if (j.rank > 2) return { amt: 0, how: 'past the 2nd job for this customer, not paid' };
  const rate = c.manager ? 0.03 : (j.role === 'Telemarketer' || j.role === 'Confirmer') ? 0.0175 : c.kaleb ? 0.015 : 0.0125;
  return { amt: j.value * j.share * rate, how: `${(rate * 100).toFixed(2).replace(/\.?0+$/, '')}% of the job` };
}

export default function MyCommission({ c, today, dim, preview, onGoalSaved }) {
  const month = today.slice(0, 7), year = today.slice(0, 4);
  const [show, setShow] = useState('month');
  const [g, setG] = useState(c.goal || 0);
  const [busy, setBusy] = useState(false);

  const rows = c.jobs.map(j => ({ ...j, ...commissionFor(j, c) }));
  const sum = list => list.reduce((s, r) => s + (r.amt || 0), 0);
  const inMonth = rows.filter(r => r.sold.slice(0, 7) === month), inYear = rows.filter(r => r.sold.slice(0, 4) === year);
  const mo = sum(inMonth), yr = sum(inYear);
  const waiting = sum(inYear.filter(r => !r.funded));
  const missing = rows.filter(r => r.amt == null);
  const moSales = inMonth.reduce((s, r) => s + r.revenue * r.share, 0);

  // What a dollar of sales pays this person: commission ÷ their share of sales, on the last 12 months'
  // jobs whose commission is known.
  const known = rows.filter(r => r.amt != null);
  const knownSales = known.reduce((s, r) => s + r.revenue * r.share, 0);
  const R = knownSales > 0 && sum(known) > 0 ? sum(known) / knownSales : null;
  const avgTicket = known.length ? knownSales / known.length : null;

  const day = Number(today.slice(8)), pace = g * day / dim;
  const done = g ? Math.min(mo / g, 1) : 0;
  const needSales = g && R ? g / R : null;
  const leftSales = g && R ? Math.max(g - mo, 0) / R : null;
  const leftJobs = leftSales && avgTicket ? Math.ceil(leftSales / avgTicket) : null;
  const max = Math.max(50000, Math.ceil((g || 0) / 5000) * 5000);

  async function save() {
    setBusy(true);
    try { const r = await saveMyCommGoal(g); toast.success('Commission goal saved'); onGoalSaved(r.commGoal); }
    catch (e) { toast.error('Could not save your goal'); } finally { setBusy(false); }
  }
  const list = show === 'month' ? inMonth : inYear;
  const isRep = c.jobs.some(j => j.role === 'Sales Rep');

  return (
    <section className="rd-sec rd-comm" aria-labelledby="rd-comm-h">
      <div className="rd-sechead"><h2 id="rd-comm-h">My commission</h2><Link className="rd-btn small" to="/my-jobs?tab=commission">Pay history</Link></div>
      <div className="rd-comm-nums">
        <div><span className="rd-eyebrow">This month</span><b className="gold">{usd(mo)}</b><small>{inMonth.length} {inMonth.length === 1 ? 'job' : 'jobs'} sold · {usd(moSales)} in sales</small></div>
        <div><span className="rd-eyebrow">{year} so far</span><b>{usd(yr)}</b><small>{inYear.length} jobs</small></div>
        <div><span className="rd-eyebrow">Waiting for funding</span><b>{usd(waiting)}</b><small>paid once the job is funded</small></div>
      </div>

      <div className="rd-comm-goal">
        <div className="rd-pace-head"><span><span className="rd-eyebrow">Monthly commission goal</span> <strong>{usd(mo)} of {usd(g)}</strong></span>
          {g > 0 && (mo >= pace ? <span className="rd-pill good">Ahead of pace</span> : <span className="rd-pill warn">Behind pace by {usd(pace - mo)}</span>)}</div>
        <div className="rd-ctrack" role="img" aria-label={`${usd(mo)} of a ${usd(g)} goal`}>
          <div className="rd-cfill" style={{ width: `${done * 100}%` }} />
          {g > 0 && <div className="rd-cmark" style={{ left: `${Math.min(day / dim, 1) * 100}%` }} title={`Pace today ${usd(pace)}`} />}
        </div>
        {!preview && <>
          <label htmlFor="rd-cg" className="rd-note">Drag to set your goal</label>
          <input id="rd-cg" type="range" min="0" max={max} step="500" value={g} onChange={e => setG(+e.target.value)} />
        </>}
        <p className="rd-comm-need">
          {!g ? 'Set a goal to see how much you need to sell.'
            : mo >= g ? `Goal hit: ${usd(mo)} this month. Everything from here is extra.`
            : R ? <>To make <b>{usd(g)}</b> this month you need about <b>{usd(needSales)}</b> in sales. You still need <b>{usd(leftSales)}</b> more{leftJobs ? <>, about <b>{leftJobs} {leftJobs === 1 ? 'job' : 'jobs'}</b> at your {usd(avgTicket)} average</> : null}.</>
            : 'Your commission so far isn\'t worked out on enough jobs to tell how much you need to sell.'}
        </p>
        {R ? <p className="rd-note">You've been making about {(R * 100).toFixed(1)}¢ of commission per $1 sold over the last 12 months.</p> : null}
        {!preview && g !== (c.goal || 0) && <button type="button" className="rd-btn gold" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save my goal'}</button>}
      </div>

      <div className="rd-sechead" style={{ marginTop: 16 }}><h3 style={{ fontSize: '1.2rem' }}>Where it comes from</h3>
        <div className="rd-seg">{[['month', 'This month'], ['year', `${year}`]].map(([k, l]) => <button key={k} type="button" className={show === k ? 'on' : ''} onClick={() => setShow(k)}>{l}</button>)}</div></div>
      {list.length ? list.map(r => (
        <div key={r.id} className="rd-row"><div className="main"><span className="title">{r.customer || '—'}</span>{' '}
          <span className={'rd-pill ' + (r.amt == null ? 'flat' : r.funded ? 'good' : 'warn')}>{r.amt == null ? 'Not worked out yet' : r.funded ? 'Earned' : 'Waiting for funding'}</span>
          <span className="rd-sub">PO {r.po} · sold {niceDate(r.sold)} · as {r.role.toLowerCase()}{r.share < 1 ? ` · your ${Math.round(r.share * 100)}% share` : ''} · {r.how}</span></div>
          <div style={{ textAlign: 'right' }}><b style={{ font: '700 1.3rem var(--display)' }}>{r.amt == null ? '—' : usd(r.amt)}</b><span className="rd-sub">on {usd(r.revenue * r.share)}</span></div></div>))
        : <div className="rd-empty">No jobs sold {show === 'month' ? 'this month' : 'this year'} yet.</div>}
      {missing.length > 0 && isRep && <p className="rd-note" style={{ marginTop: 10 }}>{missing.length} of your jobs don't have their commission worked out yet. A sales rep's commission comes from the job's Pre-Bid tab (same formula as the Commission Calculator). Ask the office to fill it in.</p>}
      <p className="rd-note" style={{ marginTop: 6 }}>Estimates from the job records. Your paycheck is what the office processes.</p>
    </section>
  );
}
